import React, { useState, useEffect } from 'react';
import { auth } from './lib/firebase';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  onAuthStateChanged,
} from 'firebase/auth';

export default function SlateVaultAuth() {
  const [mode, setMode] = useState('signin'); // 'signin' | 'signup' | 'reset'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [status, setStatus] = useState('idle'); // 'idle' | 'loading' | 'error' | 'success'
  const [errorMsg, setErrorMsg] = useState('');

  // Redirect if already logged in
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      if (user) window.location.hash = '#/slatevault/app';
    });
    return unsub;
  }, []);

  const handleError = (err) => {
    const map = {
      'auth/invalid-credential':      'Incorrect email or password.',
      'auth/user-not-found':          'No account found with that email.',
      'auth/wrong-password':          'Incorrect password.',
      'auth/email-already-in-use':    'An account with this email already exists.',
      'auth/weak-password':           'Password must be at least 6 characters.',
      'auth/invalid-email':           'Please enter a valid email address.',
      'auth/too-many-requests':       'Too many attempts. Please try again later.',
    };
    setErrorMsg(map[err.code] || 'Something went wrong. Please try again.');
    setStatus('error');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMsg('');

    try {
      if (mode === 'signup') {
        await createUserWithEmailAndPassword(auth, email, password);
        // onAuthStateChanged will redirect to app
      } else if (mode === 'signin') {
        await signInWithEmailAndPassword(auth, email, password);
      } else if (mode === 'reset') {
        await sendPasswordResetEmail(auth, email);
        setStatus('success');
      }
    } catch (err) {
      handleError(err);
    }
  };

  const titles = {
    signin: { heading: 'Sign In', sub: 'SLATEVAULT', btn: 'SIGN IN' },
    signup: { heading: 'Create Account', sub: '$49 / MONTH', btn: 'CONTINUE TO PAYMENT →' },
    reset:  { heading: 'Reset Password', sub: 'SLATEVAULT', btn: 'SEND RESET EMAIL' },
  };

  const t = titles[mode];

  return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center p-6" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      <div className="w-full max-w-md">

        {/* Logo */}
        <div className="text-center mb-10">
          <a href="/#/" style={{ textDecoration: 'none' }}>
            <img src="/logo.png" alt="Aperture Slate" className="h-10 mx-auto mb-4 opacity-80" />
          </a>
          <p className="text-[10px] tracking-[0.4em] text-sky-400 font-bold uppercase mb-1">Aperture Slate</p>
          <p className="text-[10px] tracking-[0.4em] text-zinc-600 font-bold uppercase">{t.sub}</p>
        </div>

        {/* Card */}
        <div className="border border-zinc-800 bg-zinc-950 p-8">
          <h1 className="text-2xl font-black tracking-tight uppercase mb-6">{t.heading}</h1>

          {status === 'success' && mode === 'reset' ? (
            <div className="text-center py-6">
              <p className="text-white font-bold mb-2">Reset Email Sent</p>
              <p className="text-zinc-500 text-sm mb-6">Check your inbox for a password reset link.</p>
              <button onClick={() => { setMode('signin'); setStatus('idle'); }} className="text-[11px] tracking-widest text-sky-400 uppercase font-bold hover:text-sky-300 transition-colors">
                Back to Sign In
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-3">
              <input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setStatus('idle'); }}
                required
                className="w-full bg-zinc-900 border border-zinc-700 focus:border-sky-500 text-white text-sm px-4 py-3 outline-none transition-colors"
                style={{ fontFamily: 'inherit' }}
              />

              {mode !== 'reset' && (
                <input
                  type="password"
                  placeholder={mode === 'signup' ? 'Create a password (min 6 chars)' : 'Password'}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setStatus('idle'); }}
                  required
                  className="w-full bg-zinc-900 border border-zinc-700 focus:border-sky-500 text-white text-sm px-4 py-3 outline-none transition-colors"
                  style={{ fontFamily: 'inherit' }}
                />
              )}

              {errorMsg && (
                <p className="text-red-400 text-[11px] tracking-wide">{errorMsg}</p>
              )}

              <button
                type="submit"
                disabled={status === 'loading'}
                className="w-full bg-white text-black font-black text-[11px] tracking-widest uppercase py-3 hover:bg-sky-400 transition-colors disabled:opacity-50"
              >
                {status === 'loading' ? 'PLEASE WAIT…' : t.btn}
              </button>
            </form>
          )}

          {/* Mode switchers */}
          {status !== 'success' && (
            <div className="border-t border-zinc-800 mt-6 pt-6 space-y-3 text-center">
              {mode === 'signin' && (
                <>
                  <button onClick={() => { setMode('signup'); setStatus('idle'); setErrorMsg(''); }} className="block w-full text-[11px] tracking-widest text-zinc-500 hover:text-sky-400 uppercase transition-colors font-bold">
                    No account? Create one →
                  </button>
                  <button onClick={() => { setMode('reset'); setStatus('idle'); setErrorMsg(''); }} className="block w-full text-[11px] tracking-widest text-zinc-600 hover:text-zinc-400 uppercase transition-colors">
                    Forgot password?
                  </button>
                </>
              )}
              {mode === 'signup' && (
                <button onClick={() => { setMode('signin'); setStatus('idle'); setErrorMsg(''); }} className="block w-full text-[11px] tracking-widest text-zinc-500 hover:text-sky-400 uppercase transition-colors font-bold">
                  Already have an account? Sign in →
                </button>
              )}
              {mode === 'reset' && (
                <button onClick={() => { setMode('signin'); setStatus('idle'); setErrorMsg(''); }} className="block w-full text-[11px] tracking-widest text-zinc-500 hover:text-sky-400 uppercase transition-colors font-bold">
                  Back to sign in →
                </button>
              )}
            </div>
          )}
        </div>

        <div className="text-center mt-6">
          <a href="/#/slatevault" style={{ textDecoration: 'none' }} className="text-[10px] text-zinc-700 hover:text-zinc-500 tracking-widest uppercase transition-colors">
            ← Back to SlateVault
          </a>
        </div>
      </div>
    </div>
  );
}
